/**
 * "무엇을 틀까" 고르개 — 플레이리스트·스케줄·콘텐츠를 한 모양으로 고른다(2026-10-21).
 *
 * 브라우저 기본 `<select>` 를 대신한다(`playbackPick.ts` 머리말). 닫혀 있을 때도 무엇을 골랐는지가 **종류 아이콘·이름·
 * 한 줄 정보**로 보이고, 열면 검색칸과 종류 칸(전체·플레이리스트·스케줄·콘텐츠), 묶음별 목록이 나온다. 쓰는 곳 일곱
 * 군데가 모두 이 부품이라 어디서 고르든 모양과 손놀림이 같다.
 *
 * - 목록은 문서 끝에 띄운다(⋯ 메뉴·시각 칸과 같은 `placeMenu`) — 패널·창의 `overflow` 에 잘리지 않게.
 * - 열면 검색칸에 초점. ↑·↓ 로 줄을 옮기고 Enter 로 고른다. Esc 는 목록만 닫는다(창 안에서도 창은 그대로).
 * - 고른 것이 목록에 없으면(지웠거나 보관했다) 그 사실을 버튼에 말한다 — 빈칸이면 "없음" 으로 읽힌다.
 */
import {
  Ban,
  CalendarDays,
  Check,
  ChevronDown,
  FileCode2,
  Film,
  Globe,
  Image as ImageIcon,
  LayoutTemplate,
  ListMusic,
  Search,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { placeMenu } from './MoreMenu.js';
import {
  availableKinds,
  findRow,
  groupRows,
  KIND_LABEL,
  pickRows,
  type PickKind,
  type PickRow,
  type PickSources,
  type PickValue,
} from './playbackPick.js';

export type { PickValue } from './playbackPick.js';

function RowIcon({ row }: { row: Pick<PickRow, 'kind' | 'assetType' | 'thumb'> }): ReactNode {
  if (row.thumb) return <img className="pp-thumb" src={row.thumb} alt="" loading="lazy" />;
  const Icon =
    row.kind === 'playlist'
      ? ListMusic
      : row.kind === 'schedule'
        ? CalendarDays
        : row.assetType === 'video'
          ? Film
          : row.assetType === 'web'
            ? Globe
            : row.assetType === 'template'
              ? LayoutTemplate
              : row.assetType === 'html'
                ? FileCode2
                : ImageIcon;
  return (
    <span className={`pp-icon pp-icon--${row.kind}`} aria-hidden="true">
      <Icon className="h-3.5 w-3.5" strokeWidth={2} />
    </span>
  );
}

export function PlaybackPicker({
  value,
  onChange,
  sources,
  label,
  none,
  placeholder = '고르세요',
  disabled = false,
  className = '',
}: {
  value: PickValue | null;
  onChange: (next: PickValue | null) => void;
  /** 고를 수 있는 것 — 준 종류만 나온다. */
  sources: PickSources;
  /** 화면 낭독기에 읽히는 이름("평소 편성"). */
  label: string;
  /** 비워 둘 수 있으면 그 줄의 이름("없음(대기 화면)"). 주지 않으면 비울 수 없다. */
  none?: string;
  /** 아직 아무것도 고르지 않았을 때 버튼 글자(비울 수 없는 칸). */
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}): ReactNode {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<PickKind | 'all'>('all');
  const [active, setActive] = useState(0);

  // 원천은 부르는 쪽이 그릴 때마다 새 객체로 넘긴다 — 안의 목록이 바뀔 때만 다시 센다.
  const rows = useMemo(
    () => pickRows(sources),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 목록(배열) 단위로 본다.
    [sources.playlists, sources.schedules, sources.assets],
  );
  const kinds = availableKinds(rows);
  const current = findRow(rows, value);
  const groups = useMemo(() => groupRows(rows, query, kind), [rows, query, kind]);
  /** 키보드로 움직이는 줄 — "없음" 이 맨 앞(검색 중이 아닐 때). */
  const flat: Array<PickRow | null> = useMemo(
    () => [...(none && !query.trim() ? [null] : []), ...groups.flatMap((group) => group.rows)],
    [groups, none, query],
  );

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    setPosition(null);
    if (refocus) trigger.current?.focus();
  }, []);

  const openList = (): void => {
    if (disabled) return;
    setQuery('');
    setKind('all');
    setOpen(true);
  };

  // 열 때 지금 고른 줄에 맞춘다.
  useEffect(() => {
    if (!open) return;
    const at = flat.findIndex((row) =>
      row === null ? value === null : value !== null && row.kind === value.kind && row.id === value.id,
    );
    setActive(Math.max(0, at));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 열 때 한 번.
  }, [open]);
  // 거르면 맨 위로.
  useEffect(() => setActive(0), [query, kind]);

  useLayoutEffect(() => {
    if (!open || !trigger.current || !box.current) return;
    const a = trigger.current.getBoundingClientRect();
    const width = Math.min(Math.max(a.width, 320), window.innerWidth - 16);
    const b = box.current.getBoundingClientRect();
    // 버튼 왼쪽 끝에 맞춘다 — placeMenu 는 오른쪽 끝에 맞추므로 목록 폭만큼 넘겨 준다.
    const next = placeMenu(
      { top: a.top, bottom: a.bottom, left: a.left, right: a.left + width },
      { width, height: b.height },
      { width: window.innerWidth, height: window.innerHeight },
    );
    setPosition((prev) =>
      prev && prev.top === next.top && prev.left === next.left && prev.width === width ? prev : { ...next, width },
    );
  });

  useEffect(() => {
    if (!open) return;
    const inside = (target: EventTarget | null): boolean =>
      target instanceof Node && (trigger.current?.contains(target) === true || box.current?.contains(target) === true);
    const onPointer = (event: PointerEvent): void => {
      if (!inside(event.target)) close(false);
    };
    const onScroll = (event: Event): void => {
      if (!inside(event.target)) close(false);
    };
    const onResize = (): void => close(false);
    document.addEventListener('pointerdown', onPointer, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  // 움직인 줄이 보이게.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const pick = (row: PickRow | null): void => {
    onChange(row ? { kind: row.kind, id: row.id } : null);
    close(true);
  };

  const missing = value !== null && current === null;
  let index = none && !query.trim() ? 1 : 0;
  const optionId = (at: number): string => `${id}-opt-${at}`;

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={`pp-trigger${open ? ' is-open' : ''} ${className}`}
        disabled={disabled}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => (open ? close(false) : openList())}
        onKeyDown={(event) => {
          if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault();
            openList();
          }
        }}
      >
        {current ? (
          <>
            <RowIcon row={current} />
            <span className="pp-text">
              <span className="pp-name">{current.name}</span>
              <span className="pp-meta">
                {KIND_LABEL[current.kind]} · {current.meta}
              </span>
            </span>
          </>
        ) : missing ? (
          <>
            <span className="pp-icon pp-icon--missing" aria-hidden="true">
              <Ban className="h-3.5 w-3.5" strokeWidth={2} />
            </span>
            <span className="pp-text">
              <span className="pp-name">지워졌거나 보관된 {KIND_LABEL[value.kind]}</span>
              <span className="pp-meta">다른 것을 고르세요</span>
            </span>
          </>
        ) : (
          <span className="pp-text">
            <span className="pp-name pp-placeholder">{none ?? placeholder}</span>
          </span>
        )}
        <ChevronDown className="pp-chevron h-4 w-4" strokeWidth={2} aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <div
              ref={box}
              id={id}
              role="dialog"
              aria-label={label}
              className="pp-pop"
              style={
                position
                  ? { top: position.top, left: position.left, width: position.width }
                  : { top: -9999, left: -9999 }
              }
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  // 목록만 닫는다 — 창(Modal)이 이 키로 닫히지 않게(`defaultPrevented` 를 본다).
                  event.preventDefault();
                  close(true);
                } else if (event.key === 'Tab') {
                  event.preventDefault();
                  close(true);
                } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  event.preventDefault();
                  if (flat.length > 0)
                    setActive((at) => (at + (event.key === 'ArrowDown' ? 1 : -1) + flat.length) % flat.length);
                } else if (event.key === 'Enter') {
                  event.preventDefault();
                  if (active < flat.length) pick(flat[active] ?? null);
                }
              }}
            >
              <div className="pp-search">
                <Search className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
                <input
                  autoFocus
                  value={query}
                  placeholder="이름 검색"
                  aria-label={`${label} 검색`}
                  aria-controls={`${id}-list`}
                  aria-activedescendant={flat.length > 0 ? optionId(active) : undefined}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
              {kinds.length > 1 ? (
                <div className="pp-kinds seg seg-sm" role="group" aria-label="종류">
                  {(['all', ...kinds] as const).map((each) => (
                    <button
                      key={each}
                      type="button"
                      className="seg-btn"
                      aria-pressed={kind === each}
                      onClick={() => setKind(each)}
                    >
                      {each === 'all' ? '전체' : KIND_LABEL[each]}
                    </button>
                  ))}
                </div>
              ) : null}
              <div ref={listRef} id={`${id}-list`} className="pp-list" role="listbox" aria-label={label}>
                {none && !query.trim() ? (
                  <div
                    id={optionId(0)}
                    data-index={0}
                    role="option"
                    aria-selected={value === null}
                    className={`pp-row${active === 0 ? ' is-active' : ''}`}
                    onPointerMove={() => setActive(0)}
                    onClick={() => pick(null)}
                  >
                    <span className="pp-icon pp-icon--none" aria-hidden="true">
                      <Ban className="h-3.5 w-3.5" strokeWidth={2} />
                    </span>
                    <span className="pp-text">
                      <span className="pp-name">{none}</span>
                    </span>
                    {value === null ? (
                      <Check className="pp-check h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
                    ) : null}
                  </div>
                ) : null}
                {groups.length === 0 ? (
                  <p className="pp-empty">{query.trim() ? '검색 결과가 없습니다' : '고를 것이 없습니다'}</p>
                ) : null}
                {groups.map((group) => (
                  <div key={group.kind} role="group" aria-label={KIND_LABEL[group.kind]}>
                    <p className="pp-group">
                      {KIND_LABEL[group.kind]} <span>{group.total}</span>
                    </p>
                    {group.rows.map((row) => {
                      const at = index;
                      index += 1;
                      const selected = value !== null && value.kind === row.kind && value.id === row.id;
                      return (
                        <div
                          key={`${row.kind}:${row.id}`}
                          id={optionId(at)}
                          data-index={at}
                          role="option"
                          aria-selected={selected}
                          className={`pp-row${active === at ? ' is-active' : ''}`}
                          onPointerMove={() => setActive(at)}
                          onClick={() => pick(row)}
                        >
                          <RowIcon row={row} />
                          <span className="pp-text">
                            <span className="pp-name">{row.name}</span>
                            <span className="pp-meta">{row.meta}</span>
                          </span>
                          {selected ? (
                            <Check className="pp-check h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
                          ) : null}
                        </div>
                      );
                    })}
                    {group.total > group.rows.length ? (
                      <p className="pp-more">
                        {group.total - group.rows.length}개 더 있습니다 — 이름으로 검색해 좁히세요
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/** `playlist:<id>` · `schedule:<id>` 꼴(화면·그룹 배정의 고르기 값) ↔ 고르개 값. */
export function pickFromAssignmentKey(key: string): PickValue | null {
  if (key.startsWith('playlist:')) return { kind: 'playlist', id: key.slice('playlist:'.length) };
  if (key.startsWith('schedule:')) return { kind: 'schedule', id: key.slice('schedule:'.length) };
  return null;
}

export function assignmentKeyFromPick(value: PickValue | null): string {
  return value && value.kind !== 'asset' ? `${value.kind}:${value.id}` : '';
}
